import { useCallback, useState } from "react";
import { evaluate } from "mathjs";
import { useLaborFormulas } from "../../../hooks/query";

interface PrefilledValues {
    [laborServiceId: string]: {
        value?: string;
        quantity?: number;
    };
}

interface LaborService {
    Id: string;
    Name: string;
    CalculationFormula?: string | null;
}

export const sanitizeVariableName = (name: string): string => {
    if (!name) return "";
    return name
        .replace(/&/g, "And")
        .replace(/\//g, "Or")
        .replace(/[^a-zA-Z0-9_\s]/g, "")
        .replace(/\s+(\w)/g, (_, c) => c.toUpperCase())
        .replace(/\s+/g, "");
};

export const parseRoofPitchSq = (raw: any, totalArea: number): Record<string, number> => {
    const steepSquares = {
        SteepSquares7: 0,
        SteepSquares8: 0,
        SteepSquares9: 0,
        SteepSquares10: 0,
        SteepSquares11: 0,
        SteepSquares12: 0,
        SteepSquares13Plus: 0,
    };

    if (raw === null || raw === undefined || raw === "") return steepSquares;

    const parsePitchNumerator = (pitchStr: any): number | null => {
        if (pitchStr === null || pitchStr === undefined) return null;
        const str = String(pitchStr).trim();
        if (!str) return null;
        if (str.includes("/")) {
            const parts = str.split("/");
            const num = Number(parts[0].trim());
            if (!isNaN(num)) return num;
        }
        const num = Number(str);
        if (!isNaN(num)) return num;
        return null;
    };

    let parsed: any;
    if (typeof raw === "string") {
        try {
            parsed = JSON.parse(raw);
        } catch {
            parsed = null;
        }
    } else {
        parsed = raw;
    }

    if (Array.isArray(parsed)) {
        parsed.forEach((segment: any) => {
            if (!segment) return;
            const pitchNum = parsePitchNumerator(segment.pitch);
            const sq = Number(segment.squares) || 0;
            if (pitchNum !== null) {
                if (pitchNum === 7) steepSquares.SteepSquares7 += sq;
                else if (pitchNum === 8) steepSquares.SteepSquares8 += sq;
                else if (pitchNum === 9) steepSquares.SteepSquares9 += sq;
                else if (pitchNum === 10) steepSquares.SteepSquares10 += sq;
                else if (pitchNum === 11) steepSquares.SteepSquares11 += sq;
                else if (pitchNum === 12) steepSquares.SteepSquares12 += sq;
                else if (pitchNum >= 13) steepSquares.SteepSquares13Plus += sq;
            }
        });
    } else {
        const pitchNum = parsePitchNumerator(raw);
        if (pitchNum !== null) {
            if (pitchNum === 7) steepSquares.SteepSquares7 = totalArea;
            else if (pitchNum === 8) steepSquares.SteepSquares8 = totalArea;
            else if (pitchNum === 9) steepSquares.SteepSquares9 = totalArea;
            else if (pitchNum === 10) steepSquares.SteepSquares10 = totalArea;
            else if (pitchNum === 11) steepSquares.SteepSquares11 = totalArea;
            else if (pitchNum === 12) steepSquares.SteepSquares12 = totalArea;
            else if (pitchNum >= 13) steepSquares.SteepSquares13Plus = totalArea;
        }
    }

    return steepSquares;
};

export const parseChimneysSq = (raw: any): Record<string, number> => {
    const counts = {
        BrickChimneys: 0,
        WoodChimneys: 0,
    };
    if (raw === null || raw === undefined || raw === "") return counts;

    let parsed: any;
    if (typeof raw === "string") {
        try {
            parsed = JSON.parse(raw);
        } catch {
            parsed = null;
        }
    } else {
        parsed = raw;
    }

    if (Array.isArray(parsed)) {
        parsed.forEach((entry: any) => {
            if (!entry) return;
            const typeStr = String(entry.type || "").toLowerCase().trim();
            const count = Number(entry.count) || 0;
            if (typeStr === "brick") {
                counts.BrickChimneys += count;
            } else if (typeStr === "siding" || typeStr === "wood") {
                counts.WoodChimneys += count;
            }
        });
    }
    return counts;
};

const useLaborFormulaEvaluator = () => {
    const [computedValues, setComputedValues] = useState<Record<string, number | null>>({});
    const { useList } = useLaborFormulas();
    const { data: laborFormulas, isLoading: isLoadingLaborFormulas } = useList();

    const evaluateFormula = useCallback(
        (
            measurements: any[],
            laborServices: LaborService[],
            _prefilledValues: PrefilledValues,
            attributes?: any[],
            cartItems?: any[],
        ): Record<string, number | null> => {
            const results: Record<string, number | null> = {};
            const measurementsObject = convertMeasurementIntoKeyValuePair(measurements);
            const totalArea = Number(measurementsObject.Area) || 0;
            const defaultChimneyCount = Number(measurementsObject.Chimney || measurementsObject.Chimneys) || 0;
            const attributesScope = convertAttributesIntoScope(attributes, totalArea, defaultChimneyCount);
            
            const materialsScope: Record<string, number> = {};
            if (cartItems) {
                cartItems.forEach((item) => {
                    if (item.ProductCategory) {
                        const sanitizedCategory = sanitizeVariableName(item.ProductCategory);
                        materialsScope[sanitizedCategory] = (materialsScope[sanitizedCategory] || 0) + (Number(item.ItemCount) || 0);
                    }
                });
            }

            const scope = { ...measurementsObject, ...attributesScope, ...materialsScope };

            laborServices.forEach((service) => {
                // First try: use the formula stored on the service record (from DB)
                // Second try: fall back to the formula from the backend labor-formulas lookup by service Name
                const expression =
                    service.CalculationFormula ||
                    (laborFormulas && !isLoadingLaborFormulas
                        ? laborFormulas[service.Name]?.expression
                        : null);

                if (expression) {
                    try {
                        let result = evaluate(expression, scope) as number;

                        // Apply output format — prefer backend definition, default to WholeNumber
                        const outputFormat =
                            laborFormulas && laborFormulas[service.Name]?.outputFormat;
                        if (!outputFormat || outputFormat === "WholeNumber") {
                            result = Math.ceil(result);
                        }

                        results[service.Id] = result;
                    } catch (error) {
                        results[service.Id] = 0;
                    }
                } else {
                    results[service.Id] = 0;
                }
            });

            setComputedValues(results);
            return results;
        },
        [laborFormulas, isLoadingLaborFormulas],
    );

    const convertMeasurementIntoKeyValuePair = (measurements: any) => {
        if (!measurements) return getDefaultValues();

        const result = measurements.reduce((acc: any, measurement: any) => {
            const value = measurement.ActualValue ?? measurement.CalculatedValue;
            const sanitizedKey = sanitizeVariableName(measurement.MeasurementType);
            acc[sanitizedKey] = Number(value) || 0;
            // Also keep original name with spaces for compatibility
            acc[measurement.MeasurementType] = Number(value) || 0;
            return acc;
        }, {});

        return { ...getDefaultValues(), ...result };
    };

    const getDefaultValues = () => ({
        Area: 0,
        Eave: 0,
        Rake: 0,
        Ridge: 0,
        Hip: 0,
        Valley: 0,
        Headwall: 0,
        Step: 0,
        RollMetal: 0,
        CounterFlashing: 0,
        RoofVent: 0,
        RVs: 0,
        Skylight: 0,
        Skylights: 0,
        Chimney: 0,
        PipeJack: 0,
        Pjack: 0,
        SplitBoots: 0,
        SplitsBoots: 0,
        FurnaceVents: 0,
        Furn: 0,
        BathroomVents: 0,
        BathVents: 0,
        DryerVents: 0,
        GutterLength: 0,
        DownspoutLength: 0,
        SolarPanels: 0,
        Cricket: 0,
        Cooler: 0,
        LowSlope: 0,
        TearOffLayer: 1,
    });

    const convertAttributesIntoScope = (attributes?: any[], totalArea: number = 0, defaultChimneyCount: number = 0): Record<string, any> => {
        const scope: Record<string, any> = {
            SteepSquares7: 0,
            SteepSquares8: 0,
            SteepSquares9: 0,
            SteepSquares10: 0,
            SteepSquares11: 0,
            SteepSquares12: 0,
            SteepSquares13Plus: 0,
            BrickChimneys: 0,
            WoodChimneys: 0,
            HasEvaporativeCooler: 0,
        };
        if (!attributes || attributes.length === 0) return scope;

        let parsedChimneyDetails = false;

        attributes.forEach((attr) => {
            const rawName: string = attr.AttributeName || attr.Name || attr.key;
            if (!rawName) return;
            const sanitizedKey = sanitizeVariableName(rawName);
            const camelKey = rawName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+(\w)/g, (_: string, c: string) => c.toUpperCase());
            
            const raw = attr.AttributeValue !== undefined ? attr.AttributeValue : (attr.Value !== undefined ? attr.Value : attr.value);
            let value: any;
            if (raw === null || raw === undefined || raw === "") {
                if (attr.Qty !== undefined && attr.Qty !== null && attr.Qty !== "") {
                    value = Number(attr.Qty) || 0;
                } else {
                    return;
                }
            } else {
                if (rawName.toLowerCase() === "roof pitch") {
                    const steepMap = parseRoofPitchSq(raw, totalArea);
                    Object.assign(scope, steepMap);
                }

                if (rawName.toLowerCase() === "chimney details") {
                    const chimneyMap = parseChimneysSq(raw);
                    Object.assign(scope, chimneyMap);
                    parsedChimneyDetails = true;
                }

                if (rawName.toLowerCase() === "has evaporative cooler") {
                    value = (raw === "Yes") ? 1 : 0;
                } else {
                    let parsed: any;
                    try {
                        parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                    } catch {
                        parsed = null;
                    }

                    if (Array.isArray(parsed)) {
                        const total = parsed.reduce((sum: number, item: any) => sum + (Number(item.count) || 0), 0);
                        value = total > 0 ? total : parsed.length;
                    } else {
                        // Plain string — convert to number if possible, also parse fractions like "6/12"
                        let num = Number(raw);
                        if (isNaN(num) && typeof raw === "string" && raw.includes("/")) {
                            const parts = raw.split("/");
                            if (parts.length === 2) {
                                const num1 = Number(parts[0].trim());
                                const num2 = Number(parts[1].trim());
                                if (!isNaN(num1) && !isNaN(num2) && num2 !== 0) {
                                    num = num1 / num2;
                                }
                            }
                        }
                        value = !isNaN(num) && raw !== "" ? num : raw;
                    }
                }
            }
            scope[sanitizedKey] = value;
            scope[camelKey] = value;
        });

        if (!parsedChimneyDetails) {
            const chimneyType = String(scope.ChimneyType || "").toLowerCase().trim();
            const chimneyCount = Number(scope.Chimney !== undefined ? scope.Chimney : defaultChimneyCount) || 0;
            if (chimneyCount > 0) {
                if (chimneyType === "brick") {
                    scope.BrickChimneys = chimneyCount;
                } else if (chimneyType === "siding" || chimneyType === "wood") {
                    scope.WoodChimneys = chimneyCount;
                }
            }
        }

        return scope;
    };

    return { computedValues, evaluateFormula, isLoadingLaborFormulas };

};

export default useLaborFormulaEvaluator;
